<template>
	<main class="pt-3">
		<!-- DESKTOP DESIGN -->
		<template v-if="!$isMobile.any">
			<!-- filter -->
			<section class="pb-5 px-5 mx-auto max-w-[1400px]">
				<InfoBox v-if="jobListData.showInfoBox" class="mt-4 overwrite-margin">
					Dein Profil. Deine Jobs. Swipe dich durch unsere Jobangebote und bewirb dich ohne
					Anschreiben für Top-Unternehmen. Erstelle deinen hokify Lebenslauf oder lade deinen
					Lebenslauf hoch und finde innerhalb weniger Minuten deinen Traumjob!
				</InfoBox>
				<form
					id="jobsearch"
					data-type="text"
					class="flex items-center mb-2"
					@submit.stop.prevent="handleJobSearch(true)"
				>
					<div class="w-full max-w-3xl flex items-center space-x-3 mr-6">
						<Autocomplete
							id="jobsearch-keyword"
							ref="branchAutocomplete"
							v-model="jobListData.currentJobField"
							value-label="term"
							:value-promise="ontologyKeywordSuggestion"
							:min-length="2"
							:must-select="false"
							:autofocus="false"
							fullwidth="always"
							class="w-full"
							question="Beruf, Firma oder Stichwort eingeben"
							data-cy="jobsearchKeyword"
							@input="checkForTypeCompany"
						>
							Beruf, Firma, Stichwort
							<template #suggestion="slotProps">
								<div class="flex justify-between items-start">
									<span class="text-left pr-1">{{ slotProps.suggestion }}</span>
									<span
										v-if="slotProps.value.type"
										class="bg-main text-white text-xs px-2 py-1 rounded-lg"
										>{{ mapSuggestionType(slotProps.value.type) }}</span
									>
								</div>
							</template>
						</Autocomplete>

						<LocationAutocomplete
							id="jobsearch-location"
							ref="locationAutocomplete"
							v-model="jobListData.currentLocationName"
							:current-location-button="true"
							:autofocus="false"
							:resolve-geo-coords="resolveGeoCoords"
							:country="rootStore.topLevelDomain"
							class="grow max-w-xl"
							question="Gewünschten Ort eingeben"
							data-cy="jobsearchLocation"
							>Ort
						</LocationAutocomplete>

						<HokSelect
							:value="jobListData.currentRadius"
							data-cy="radius"
							:value-promise="radius"
							:disabled="!jobListData.currentLocationName"
							label="Umkreis"
							choose-text="Umkreis"
							class="flex-none w-32"
							@input="setRadius"
						/>
					</div>
					<HokButton
						id="jobsearch-button"
						submit
						:outline="!!commonData.jobDetail"
						color="main"
						class="hidden md:block flex-none m-0"
						size="big"
						data-cy="jobsearchButton"
					>
						Jobs finden
					</HokButton>
					<button
						type="submit"
						form="jobsearch"
						class="md:hidden highlight bg-main rounded-full h-12 w-12 flex justify-center flex-none"
						aria-label="Jobs finden"
					>
						<HokIcon name="icon:jobsearch" color="white" />
					</button>
				</form>
				<div class="flex space-x-4">
					<JobFilterWrapper
						:available-filters="currentAvailableFilters"
						:current-selected-filters="jobListData.currentSelectedFilters"
						@update-selected-filters="updateSelectedFilters"
					/>
					<div
						v-if="
							jobListData.currentSelectedFilters && jobListData.currentSelectedFilters.length > 0
						"
						class="relative inline-flex items-center border border-grey-lightest box-content hover:bg-grey-lightest rounded-md px-4 py-3 cursor-pointer"
						@click="resetFilters"
					>
						<h4 class="text-sm mb-0 font-normal mr-2">Filter verwerfen</h4>
						<HokIcon color="blue-grey" pointer :size="3" name="icon:trash"></HokIcon>
					</div>
				</div>
			</section>
			<!-- joblist main content -->
			<section class="pb-6 bg-grey-lightest">
				<div id="joblist-main" class="flex mx-auto pt-3 max-w-[1400px]">
					<div
						class="pt-3 bg-white flex-none border-r-8 border-grey-lightest"
						:class="commonData.jobDetail ? 'w-1/3' : 'w-1/2'"
						style="transition: width 0.5s ease-in-out"
					>
						<div>
							<div class="px-5">
								<NotificationBox v-if="commonData.jobgone" class="mb-5">
									<template #title> Job nicht gefunden! </template>
									<template #text>
										Die gesuchte Stelle ist leider nicht mehr verfügbar. Hier findest du ähnliche
										Jobs passend zu deiner Suche.
									</template>
								</NotificationBox>
								<h1
									v-if="!jobListData.error && !$isMobile.any"
									id="headline"
									class="text-left text-xl max-w-xl text-text mb-3"
								>
									{{ jobListResult?.relationCount }}
									<HokLink
										v-if="
											jobListResult?.selected?.branch?.formatted &&
											jobListResult?.selected.branch.url
										"
										:to="jobListResult?.selected.branch.url"
										link-type="internal"
										class="text-text"
										>{{ jobListResult?.selected.branch.formatted }}</HokLink
									>
									<template
										v-else-if="
											jobListResult?.selected?.branch && jobListResult?.selected.branch.formatted
										"
									>
										{{ jobListResult?.selected.branch.formatted }}
									</template>
									{{
										!jobListResult ||
										!jobListResultRelationCountNumber ||
										jobListResult?.relations.length !== 1
											? 'Jobs'
											: 'Job'
									}}
									<HokLink
										v-if="
											jobListResult?.selected?.city?.formatted && jobListResult?.selected.city.url
										"
										:to="jobListResult?.selected.city.url"
										link-type="internal"
										class="text-text"
										>in {{ jobListResult?.selected.city.formatted }}</HokLink
									>
									<template v-else-if="jobListResult?.selected?.city?.formatted">
										in {{ jobListResult?.selected.city.formatted }}
									</template>
								</h1>
								<h3
									v-if="!$isMobile.any && jobListData.showCurrentPage"
									class="text-base text-text mb-2"
								></h3>
							</div>
							<JobList
								v-if="
									!jobListData.error && jobListResult?.relations && jobListResult?.relations.length
								"
								id="joblist-online"
								:relations="jobListResult?.relations"
								:active-job="commonData.jobDetail?.jobNr ?? undefined"
								:job-list-debug="jobListData.jobListDebug"
								:show-description="false"
								:saved-matches="savedMatches"
								:logged-in="loginStore.loggedIn"
								:current-page="jobListData.currentPage"
								:usp-cards="uspCards"
								:cv-search-card="cvSearchCard"
								:satisfactory-tracking="true"
								:region="rootStore.topLevelDomain"
								:from-route="uiStateStore.fromRoute"
								prevent-job-redirect
								monochrome
								show-tags
								:is-user-app="false"
								:class="{ 'opacity-50': joblistLoading }"
								data-cy="jobList"
								@row-item-clicked="rowItemClicked"
								@save-job="saveJob"
								@jobsearch-feedback-clicked="jobsearchFeedback"
								@company-clicked="companyClicked"
								@cv-search-clicked="trackCvSearch"
							/>
							<div v-else-if="!joblistLoading" class="px-5 py-3 border-b-2 border-grey-light">
								<h2 class="text-text w-4/5">
									Deine Suche
									<strong v-if="jobListData.currentJobField"
										>"{{ jobListData.currentJobField }}"</strong
									>
									erzielte leider keine Treffer <span class="inline-block">:(</span>
								</h2>
								<div class="mb-20">
									<template
										v-if="
											jobListData.currentSelectedFilters &&
											jobListData.currentSelectedFilters.length > 0
										"
									>
										<p>
											Überprüfe deinen Suchbegriff auf Korrektheit, verwende allgemeinere Begriffe
											oder setze deine Filter zurück.
										</p>
										<p class="text-main hover:underline cursor-pointer" @click="resetFilters">
											Filter zurücksetzen
										</p>
									</template>
									<div v-else>
										<p>
											Überprüfe deinen Suchbegriff auf Korrektheit oder verwende allgemeinere
											Begriffe.
										</p>
									</div>
								</div>
								<div>
									<h4 class="text-xl">Suche nach Anstellungsart</h4>
									<div class="flex">
										<HokLink
											v-for="(category, index) in jobCategories"
											:key="index"
											:to="category.link"
											link-type="internal"
											class="text-text text-sm tracking-wide bg-grey-lightest hover:bg-grey-light px-3 py-1 rounded-full mb-2 mr-2"
											>{{ category.name }}
										</HokLink>
									</div>
								</div>
							</div>
							<Pagination
								v-if="
									!jobListData.error &&
									jobListResult &&
									jobListResult?.relations &&
									jobListResult?.relations.length &&
									jobListData.enableNotifications
								"
								:current-page="jobListData.currentPage"
								:page-count="jobListData.pageCount"
								class="self-center mb-5 pt-3"
								arrow-pagination
								data-cy="pagination"
								@page-nav="handlePageNav"
							/>
							<div
								v-else-if="!jobListData.initialJobField"
								class="flex justify-center items-center p-5 border-b-2 border-grey-lightest"
							>
								<HokIcon :size="5" name="icon:info" color="blue-grey" class="mr-2" />
								<p class="mb-0 text-xs">
									Bitte gib einen Suchbegriff ein, um alle relevanten Jobs zu sehen.
								</p>
							</div>
							<template v-if="maxPastJobs">
								<h4 class="px-5">Weitere bereits vergebene Jobs</h4>
								<JobList
									styling="offline"
									:jobs="maxPastJobs"
									:show-description="false"
									:is-user-app="false"
									monochrome
									class="mb-5 transition-opacity duration-150 ease-linear"
									:class="{ 'opacity-50': joblistLoading }"
								/>
							</template>
							<div
								v-if="jobListData.enableNotifications && jobListResultRelationCountNumber > 0"
								class="flex justify-center bg-grey-background py-12 px-4"
							>
								<div class="w-[358px]">
									<div class="mb-6 shadow-xl rounded-lg bg-coral" data-cy="openJobalarm">
										<form
											class="mb-5 px-4 py-6 flex flex-col items-center"
											@submit.stop.prevent="activateJobAlarm('cta-button')"
										>
											<div class="flex mb-6">
												<h3 class="text-base mb-0 text-white">
													Erhalte neue
													<span v-if="jobListData.currentJobField" class="font-bold"
														>{{ jobListData.currentJobField }}
													</span>
													Jobs
													<span v-if="jobListData.initialLocationName" class="font-bold">
														in {{ jobListData.initialLocationName }}</span
													>
													per E-Mail oder Push-Benachrichtigung!
												</h3>
												<div class="ml-4">
													<div
														class="bg-white/20 flex items-center justify-center rounded-full w-8 h-8"
													>
														<HokIcon :size="5" name="icon:jobalarm" color="white" />
													</div>
												</div>
											</div>
											<div class="flex justify-center">
												<HokButton submit data-cy="openJobalarm" color="white-text">
													Jetzt Jobalarm aktivieren
												</HokButton>
											</div>
										</form>
									</div>
									<div>
										<p class="flex items-center mb-2 px-2 text-sm">
											<HokIcon name="icon:checkmark-gradient" class="mr-4" />
											Verpasse nie wieder neue, passende Jobs
										</p>
										<p class="flex items-center mb-2 space-x-4 px-2 text-sm">
											<HokIcon name="icon:checkmark-gradient" class="mr-4" />
											Kein Spam oder unnötige Werbung
										</p>
									</div>
								</div>
							</div>
						</div>
					</div>
					<div
						class="bg-white flex-none border-l-8 border-grey-lightest"
						:class="commonData.jobDetail ? 'w-2/3' : 'w-1/2'"
					>
						<div
							class="top-[6.5rem]"
							:class="[
								{ 'opacity-50': joblistLoading },
								commonData.jobDetail ? 'sticky' : '[@media(height>=900px)]:sticky'
							]"
						>
							<div v-if="joblistLoading" class="absolute inset-0 mt-40">
								<Spinner :fixed="false" />
							</div>
							<ClientOnly>
								<transition name="slide-fade" mode="out-in">
									<JobDetail
										v-if="
											commonData.jobDetail &&
											commonData.additionalJobData &&
											commonData.additionalSettings &&
											commonData.jobOverviewLps
										"
										key="job-detai"
										:job="commonData.jobDetail"
										:additional-job-data="commonData.additionalJobData"
										:additional-settings="commonData.additionalSettings"
										:job-overview-lps="commonData.jobOverviewLps"
										:similar-jobs="commonData.similarJobs"
										:saved-matches="savedMatches"
										show-save-button
										:logged-in="loginStore.loggedIn"
										:job-is-saved="jobIsSaved"
										:gnc-calculation="gncCalculation"
										:user-image="userImage"
										:region="rootStore.topLevelDomain"
										:from-route="uiStateStore.fromRoute"
										data-cy="jobDetail"
										@setup-jobalarm="activateJobAlarm('cta-button', true)"
										@send-job="setupSendJob"
										@send-job-completed="
											$trackWebsiteEvent?.('send_job_completed', { itemType: $event })
										"
										@row-item-clicked="rowItemClicked"
										@save-job="saveJob"
										@company-clicked="companyClicked"
										@send-gnc="calcGnc"
										@go-to-jobsearch="openJobSearchPage"
										@acts-invite="activeSourcingInvite"
										@show-map-dropdown="trackCommute('dropdown')"
										@show-map-image="trackCommute('image')"
										@new-search-via-tag="newSearchViaTag"
									/>
									<div
										v-else
										key="fallback"
										class="w-full flex flex-col justify-center px-36 py-16"
									>
										<HokIcon
											name="icon:lightbulb-gradient-2"
											:size="14"
											class="bg-grey-background p-3 rounded-full mb-6"
										/>
										<div class="text-grey text-xl mb-14">
											Scrolle durch deine Jobvorschläge. Ein Klick öffnet die Inseratsdetails.
										</div>

										<div class="flex flex-col gap-y-8">
											<div v-if="showOtherTermsLocationHierarchy">
												<h4 class="text-grey">Regionale Jobsuche</h4>
												<ul class="list-disc list-outside px-5">
													<li
														v-for="place in jobListData.otherTerms.interlinking &&
														jobListData.otherTerms.interlinking.locationHierarchy"
														:key="place.name"
														class="my-2 text-main"
													>
														<HokLink
															v-if="place.name && place.url"
															:to="place.url"
															:invisible="place.linkType === 'invisible'"
															class="hover:underline hover:text-main-hover"
															link-type="internal"
														>
															{{ `${place.name}${place.results ? ` (${place.results})` : ''}` }}
														</HokLink>
														<span v-else>{{ place.name }}</span>
													</li>
												</ul>
											</div>
											<div v-if="showOtherTermsJobField">
												<h4 class="text-grey">Ähnliche Suchbegriffe</h4>
												<ul class="list-disc list-outside px-5">
													<li
														v-for="field in jobListData.otherTerms.interlinking &&
														jobListData.otherTerms.interlinking.jobField"
														:key="field.name"
														class="my-2 text-main"
													>
														<HokLink
															v-if="field.name && field.url"
															:to="field.url"
															:invisible="field.linkType === 'invisible'"
															class="hover:underline hover:text-main-hover"
															link-type="internal"
														>
															{{ `${field.name}${field.results ? ` (${field.results})` : ''}` }}
														</HokLink>
														<span v-else>{{ field.name }}</span>
													</li>
												</ul>
											</div>
											<div v-if="showOtherTermsSearches">
												<h4 class="text-grey">Aktuelle Jobsuchen</h4>
												<ul class="list-disc list-outside px-5">
													<li
														v-for="search in jobListData.otherTerms.searches"
														:key="search.name"
														class="my-2 text-main"
													>
														<HokLink
															v-if="search.name && search.url"
															:to="search.url"
															:invisible="search.linkType === 'invisible'"
															link-type="internal"
															class="hover:underline hover:text-main-hover"
														>
															{{ search.name }}
														</HokLink>
														<span v-else>{{ search.name }}</span>
													</li>
												</ul>
											</div>
										</div>
									</div>
								</transition>
								<template #fallback>
									<div key="fallback" class="w-full flex flex-col justify-center px-36 py-16">
										<HokIcon
											name="icon:lightbulb-gradient-2"
											:size="14"
											class="bg-grey-background p-3 rounded-full mb-6"
										/>
										<div class="text-grey text-xl mb-14">
											Scrolle durch deine Jobvorschläge. Ein Klick öffnet die Inseratsdetails.
										</div>

										<div class="flex flex-col gap-y-8">
											<div v-if="showOtherTermsLocationHierarchy">
												<h4 class="text-grey">Regionale Jobsuche</h4>
												<ul class="list-disc list-outside px-5">
													<li
														v-for="place in jobListData.otherTerms.interlinking &&
														jobListData.otherTerms.interlinking.locationHierarchy"
														:key="place.name"
														class="my-2 text-main"
													>
														<HokLink
															v-if="place.name && place.url"
															:to="place.url"
															:invisible="place.linkType === 'invisible'"
															class="hover:underline hover:text-main-hover"
															link-type="internal"
														>
															{{ `${place.name}${place.results ? ` (${place.results})` : ''}` }}
														</HokLink>
														<span v-else>{{ place.name }}</span>
													</li>
												</ul>
											</div>
											<div v-if="showOtherTermsJobField">
												<h4 class="text-grey">Ähnliche Suchbegriffe</h4>
												<ul class="list-disc list-outside px-5">
													<li
														v-for="field in jobListData.otherTerms.interlinking &&
														jobListData.otherTerms.interlinking.jobField"
														:key="field.name"
														class="my-2 text-main"
													>
														<HokLink
															v-if="field.name && field.url"
															:to="field.url"
															:invisible="field.linkType === 'invisible'"
															class="hover:underline hover:text-main-hover"
															link-type="internal"
														>
															{{ `${field.name}${field.results ? ` (${field.results})` : ''}` }}
														</HokLink>
														<span v-else>{{ field.name }}</span>
													</li>
												</ul>
											</div>
											<div v-if="showOtherTermsSearches">
												<h4 class="text-grey">Aktuelle Jobsuchen</h4>
												<ul class="list-disc list-outside px-5">
													<li
														v-for="search in jobListData.otherTerms.searches"
														:key="search.name"
														class="my-2 text-main"
													>
														<HokLink
															v-if="search.name && search.url"
															:to="search.url"
															:invisible="search.linkType === 'invisible'"
															link-type="internal"
															class="hover:underline hover:text-main-hover"
														>
															{{ search.name }}
														</HokLink>
														<span v-else>{{ search.name }}</span>
													</li>
												</ul>
											</div>
										</div>
									</div>
								</template>
							</ClientOnly>
						</div>
					</div>
				</div>
			</section>
			<!--jobalarm-sidebar-->
			<div class="absolute top-[178px] right-0 h-full">
				<div
					v-if="jobListData.enableNotifications && jobListResultRelationCountNumber > 0"
					class="[@media(width<=1550px)]:hidden sticky top-20 right-0 px-3 py-4 bg-coral rounded-l-lg flex flex-col justify-center items-center space-y-3 cursor-pointer"
					@click="activateJobAlarm('sidebar-cta')"
				>
					<p class="m-0 pt-2 text-white font-bold [writing-mode:vertical-lr] rotate-180">
						Jobalarm aktivieren!
					</p>
					<div class="rounded-full bg-white/20 w-8 h-8 flex items-center justify-center">
						<HokIcon class="-rotate-90" color="white" :size="5" name="icon:jobalarm" />
					</div>
				</div>
			</div>
		</template>

		<!-- MOBILE DESIGN -->
		<template v-else>
			<InfoBox v-if="jobListData.showInfoBox" class="mx-5 reset-margin">
				Dein Profil. Deine Jobs. Swipe dich durch unsere Jobangebote und bewirb dich ohne
				Anschreiben für Top-Unternehmen.
			</InfoBox>
			<transition name="slide-fade" mode="out-in">
				<div
					v-show="true"
					class="overflow-container w-full pt-4 bg-white shadow-md"
					:class="scrollUp ? 'sticky top-0 z-10' : ''"
				>
					<div id="jobsearch-keyword" class="flex items-center pb-4 bg-white px-5">
						<div
							class="px-2 py-2 rounded-sm border border-main flex items-center grow truncate"
							@click="openJobSearch"
						>
							<HokIcon color="grey" pointer name="icon:jobsearch" />
							<h2 v-if="searchBarValue" class="ml-2 text-sm text-text bg-white mb-0">
								{{ searchBarValue }}
							</h2>
						</div>
					</div>
					<div class="w-full items-center flex justify-between pb-4 px-5">
						<h1
							v-if="!jobListData.error"
							id="headline"
							class="text-left text-xl max-w-xl text-text mb-0"
						>
							{{ jobListResultRelationCountNumber }}
							<HokLink
								v-if="
									jobListResult?.selected?.branch?.formatted && jobListResult?.selected?.branch?.url
								"
								:to="jobListResult?.selected.branch.url"
								link-type="internal"
								class="text-text"
								>{{ jobListResult?.selected.branch.formatted }}</HokLink
							>
							<template v-else-if="jobListResult?.selected?.branch?.formatted">
								{{ jobListResult?.selected.branch.formatted }}
							</template>
							{{ jobListResult?.relations?.length !== 1 ? 'Jobs' : 'Job' }}
							<HokLink
								v-if="
									jobListResult?.selected?.city?.formatted && jobListResult?.selected?.city?.url
								"
								:to="jobListResult?.selected.city.url"
								link-type="internal"
								class="text-text"
								>in {{ jobListResult?.selected.city.formatted }}</HokLink
							>
							<template v-else-if="jobListResult?.selected?.city?.formatted">
								in {{ jobListResult?.selected.city.formatted }}
							</template>
						</h1>

						<button
							v-if="hasAvailableFilters"
							type="button"
							class="highlight flex items-center justify-center flex-none"
							@click="openFilters"
						>
							<span
								v-if="
									jobListData.currentSelectedFilters && jobListData.currentSelectedFilters.length
								"
								class="text-grey bg-grey-lightest text-xs h-5 w-5 flex items-center justify-center rounded-full p-1 tracking-wider"
								>{{ `${jobListData.currentSelectedFilters.length}+` }}</span
							>
							<HokIcon
								:size="$isMobile.any ? 6 : 10"
								color="main"
								pointer
								name="icon:filter"
								@click="trackFilter()"
							/>
						</button>
					</div>
					<!--quick filters-->
					<div
						v-if="
							(quickFilters && quickFilters.length) ||
							(jobListData.currentSelectedFilters && jobListData.currentSelectedFilters.length)
						"
						class="overflow-x-scroll hide-scrollbar flex whitespace-nowrap w-full px-4"
					>
						<template
							v-if="jobListData.currentSelectedFilters && jobListData.currentSelectedFilters.length"
						>
							<transition name="fade" mode="out-in">
								<div v-show="true" key="resetFilters">
									<div
										class="bg-grey-lightest rounded-full p-3 mr-2"
										@click="updateSelectedFilters([])"
									>
										<HokIcon name="icon:close-slim" :size="3" />
									</div>
								</div>
							</transition>

							<div v-for="filter in jobListData.currentSelectedFilters" :key="filter.filter">
								<p
									class="py-2 px-3 bg-main rounded-full text-sm text-white mb-4 mr-2"
									@click="updateSelectedFilters(filter)"
								>
									{{ truncateFct(shortenFilterTitles(filter), 23) }}
								</p>
							</div>
						</template>
						<template v-if="quickFilters && quickFilters.length">
							<div v-for="filter in quickFilters" :key="filter.title">
								<p
									class="py-2 px-3 bg-grey-lightest rounded-full text-sm mb-4 mr-2 border border-grey-darker"
									:class="{ hidden: filterActive(filter) }"
									@click="setQuickFilter(filter)"
								>
									{{ filter.title }}
								</p>
							</div>
						</template>
					</div>
				</div>
			</transition>
			<div class="px-0 md:px-5 py-0 sm:py-5 flex flex-col lg:flex-row">
				<section
					class="relative pt-0 w-full lg:w-4/5 flex flex-col transition-opacity duration-150 ease-linear"
					:class="{ 'opacity-50': joblistLoading }"
				>
					<Spinner v-if="joblistLoading" />
					<NotificationBox v-if="commonData.jobgone" class="mb-5">
						<template #title> Meldung </template>
						<template #text> Die gesuchte Stelle ist leider nicht mehr verfügbar. </template>
					</NotificationBox>
					<JobList
						v-if="!jobListData.error && jobListResult && jobListResult?.relations.length"
						id="joblist-online"
						:relations="jobListResult.relations"
						:job-list-debug="jobListData.jobListDebug"
						:show-description="!$isMobile.phone"
						:saved-matches="savedMatches"
						:logged-in="loginStore.loggedIn"
						:usp-cards="uspCards"
						:cv-search-card="cvSearchCard"
						:satisfactory-tracking="true"
						:current-page="jobListData.currentPage"
						:region="rootStore.topLevelDomain"
						:from-route="uiStateStore.fromRoute"
						monochrome
						show-tags
						:is-user-app="false"
						class="mb-1"
						data-cy="jobList"
						@row-item-clicked="rowItemClicked"
						@save-job="saveJob"
						@jobsearch-feedback-clicked="jobsearchFeedback"
						@cv-search-clicked="trackCvSearch"
					/>
					<div v-else class="px-5 py-3">
						<h2 class="text-text w-4/5">
							Deine Suche <br /><strong>"{{ jobListData.currentJobField }}"</strong> erzielte leider
							keine Treffer <span class="break-keep">:(</span>
						</h2>
						<div class="mb-20">
							<template
								v-if="
									jobListData.currentSelectedFilters &&
									jobListData.currentSelectedFilters.length > 0
								"
							>
								<p>
									Überprüfe deinen Suchbegriff auf Korrektheit, verwende allgemeinere Begriffe oder
									setze deine Filter zurück.
									<HokButton :color="'main'" is-text @click="resetFilters"
										>Filter zurücksetzen</HokButton
									>
								</p>
							</template>
							<div v-else>
								<p>
									Überprüfe deinen Suchbegriff auf Korrektheit oder verwende allgemeinere Begriffe.
								</p>
							</div>
						</div>
						<div>
							<h4 class="text-xl">Suche nach Anstellungsart</h4>
							<div class="flex">
								<HokLink
									v-for="(category, index) in jobCategories"
									:key="index"
									:to="category.link"
									link-type="internal"
									class="text-text text-sm tracking-wide bg-grey-lightest hover:bg-grey-light px-3 py-1 rounded-full mb-2 mr-2"
									>{{ category.name }}
								</HokLink>
							</div>
						</div>
					</div>
					<div
						v-if="!$isMobile.phone && jobListData.enableNotifications"
						class="p-3 sm:p-5 bg-main-light rounded-lg mb-5 text-center"
					>
						<form
							class="py-2 px-3"
							data-type="text"
							accept-charset="UTF-8"
							@submit.stop.prevent="activateJobAlarm('cta-button')"
						>
							<div class="mb-4 max-w-2xl flex mx-auto justify-center items-start">
								<HokIcon name="icon:jobalarm" color="main" />
								<h4 class="ml-2 mb-0 text-base sm:text-xl font-normal text-left overflow-hidden">
									Erhalte neue
									<span v-if="jobListData.currentJobField" class="font-bold"
										>{{ jobListData.currentJobField }}
									</span>
									<strong>Jobs</strong>
									<span v-if="jobListData.currentLocationName" class="font-bold">
										in {{ jobListData.currentLocationName }}</span
									>
									per E-Mail oder Push-Benachrichtigung!
								</h4>
							</div>
							<HokButton class="mb-4" submit color="main" fullwidth="mobile">
								Jetzt Jobalarm aktivieren
							</HokButton>
						</form>
					</div>
					<Pagination
						v-if="
							!jobListData.error &&
							jobListResult &&
							jobListResult?.relations.length &&
							jobListData.enableNotifications
						"
						:current-page="jobListData.currentPage"
						:page-count="jobListData.pageCount"
						class="self-center mb-5 pt-3"
						arrow-pagination
						data-cy="pagination"
						@page-nav="handlePageNav"
					/>
					<div
						v-else-if="!jobListData.initialJobField"
						class="flex justify-center items-center pb-5 border-b-2 border-grey-lightest"
					>
						<HokIcon :size="5" name="icon:info" color="blue-grey" class="mr-2" />
						<p class="mb-0 text-xs">
							Bitte gib einen Suchbegriff ein, um alle relevanten Jobs zu sehen.
						</p>
					</div>
					<template v-if="maxPastJobs">
						<h4 class="text-left my-4 px-6">Weitere bereits vergebene Jobs:</h4>
						<JobList
							styling="offline"
							:jobs="maxPastJobs"
							:is-user-app="false"
							:show-description="!$isMobile.phone"
							@row-item-clicked="rowItemClicked"
						/>
					</template>
					<div
						v-if="jobListData.enableNotifications"
						class="flex justify-center bg-grey-background py-12 px-4"
					>
						<div class="w-[358px]">
							<div class="mb-6 shadow-xl rounded-lg bg-coral" data-cy="openJobalarm">
								<form
									class="mb-5 px-4 py-6 flex flex-col items-center"
									@submit.stop.prevent="activateJobAlarm('cta-button')"
								>
									<div class="flex mb-6">
										<h3 class="text-base mb-0 text-white">
											Erhalte neue
											<span v-if="jobListData.currentJobField" class="font-bold"
												>{{ jobListData.currentJobField }}
											</span>
											Jobs
											<span v-if="jobListData.currentLocationName" class="font-bold">
												in {{ jobListData.currentLocationName }}</span
											>
											per E-Mail oder Push-Benachrichtigung!
										</h3>
										<div class="ml-4">
											<div
												class="bg-white/20 flex items-center justify-center rounded-full w-8 h-8"
											>
												<HokIcon :size="5" name="icon:jobalarm" color="white" />
											</div>
										</div>
									</div>
									<div class="flex justify-center">
										<HokButton submit data-cy="openJobalarm" color="white-text">
											Jetzt Jobalarm aktivieren
										</HokButton>
									</div>
								</form>
							</div>
							<div>
								<p class="flex items-center mb-2 px-2 text-sm">
									<HokIcon name="icon:checkmark-gradient" class="mr-4" />
									Verpasse nie wieder neue, passende Jobs
								</p>
								<p class="flex items-center mb-2 px-2 text-sm">
									<HokIcon name="icon:checkmark-gradient" class="mr-4" />
									Kein Spam oder unnötige Werbung
								</p>
							</div>
						</div>
					</div>
				</section>
			</div>
		</template>
		<div v-if="$isMobile.any || showSeoFooter" class="bg-grey-lightest">
			<SeoFooter :data="seoFooterData" :show-tab-view="false" />
		</div>
		<JobPreFooter data-cy="prefooter" />
		<transition name="slide-fade" mode="out-in">
			<div
				v-show="
					$isMobile.any && scrollDown && !jobListData.enableNotifications && jobListResult?.path.url
				"
				class="fixed top-0 inset-x-0 flex bg-white shadow-md p-3 sm:px-24 z-50 justify-between items-center"
				@click="goToApp"
			>
				<div class="flex items-center">
					<NuxtImg
						alt="hokify Logo"
						src="/shared-components-nuxt3/svgs/hokify/logo-hy-b2c-square.svg"
						class="aspect-square h-10 w-10 mr-2"
					/>
					<div class="mr-10">
						<p class="text-xs font-bold mb-0">hokify - Jobsuche & Bewerbung</p>
						<p class="text-xs mb-0">Gratis im Appstore</p>
					</div>
				</div>
				<HokButton class="flex-none" link-type="external" color="main" size="small"
					>LADEN</HokButton
				>
			</div>
		</transition>
		<transition name="slide-fade" mode="out-in">
			<div
				v-if="
					scrollDown && jobListData.enableNotifications && $isMobile.phone && commonData.showBar
				"
				class="fixed top-0 inset-x-0 h-12 bg-coral text-white flex justify-between items-center z-50 shadow-md px-4"
				data-cy="jobalarmOverlay"
			>
				<div class="inline-flex items-center">
					<HokIcon name="icon:jobalarm" color="white" class="mr-2" />
					Jetzt Jobalarm aktivieren
				</div>
				<HokSwitch
					id="jobalarm-switch-mobile"
					v-model="commonData.jobalarmTriggered"
					:reactive="true"
					styling="main"
					data-cy="openJobalarm"
					@input="setJobalarmTriggered"
				/>
			</div>
		</transition>
		<PopupHandler
			ref="jobPopupHandler"
			:data-obj="currentData"
			:mode="commonData.mode"
			@send-status="handleSendStatus"
			@close-button="commonData.jobalarmTriggered = false"
		/>
	</main>
</template>

<script setup lang="ts">
import JobList from '@hokify/shared-components-nuxt3/lib/components/JobList.vue';
import { format } from '@hokify/shared-components-nuxt3/lib/helpers/datehelpers/format';
import { saveLastJobsearchPath } from '@hokify/shared-components-nuxt3/lib/helpers/jobsearchNavigation';
import { lsTest } from '@hokify/shared-components-nuxt3/lib/helpers/localstorage';
import HokSwitch from '@hokify/shared-components-nuxt3/lib/components/HokSwitch.vue';
import { saveToRecentJobSearches } from '@hokify/shared-components-nuxt3/lib/helpers/jobsearch';
import JobDetail from '@hokify/shared-components-nuxt3/lib/components/JobDetail.vue';
import type {
	IAPIJobPreviewForUser,
	IAPIMatchForJobSeeker,
	IAPIMatchForJobSeekerListEntry
} from '@hokify/common';
import { truncateFct } from '@hokify/shared-components-nuxt3/lib/helpers/truncate';
import LocationAutocomplete from '@hokify/shared-components-nuxt3/lib/components/LocationAutocomplete.vue';
import InfoBox from '@hokify/shared-components-nuxt3/lib/components/InfoBox.vue';
import type { RouteLocationNormalizedGeneric } from 'vue-router';
import Autocomplete from '@hokify/shared-components-nuxt3/lib/components/Autocomplete.vue';
import HokSelect from '@hokify/shared-components-nuxt3/lib/components/HokSelect.vue';
import Pagination from '@hokify/shared-components-nuxt3/lib/components/Pagination.vue';
import { getSocialMediaMetaData } from '~/helpers/metadata';
import type { IRecentJobsearch, ISelectedFilter } from '~/stores/types';
import SeoFooter, { type ISeoFooterData } from '~/components/website/footer/SeoFooter.vue';
import { useJobList } from '@/composables/JobList';
import PopupHandler from '@/components/website/PopupHandler.vue';
import { useGncStore } from '@/stores/gnc';
import { useRelationsStore } from '@/stores/relations';
import { useUiStateStore } from '@/stores/uistate';
import { useJobsStore } from '@/stores/jobs';
import { useLoginStore } from '@/stores/login';
import { useMatchesStore } from '@/stores/matches';
import { useRootStore } from '@/stores/root';
import { useUserLandingPageStore } from '@/stores/user-landingpage';
import { definePageMeta } from '#imports';
import JobPreFooter from '~/components/website/jobs/JobPreFooter.vue';
import JobFilterWrapper from '~/components/website/jobs/JobFilterWrapper.vue';
import NotificationBox from '~/components/website/NotificationBox.vue';
import { PageNotFoundError } from '~/errors/PageNotFoundError';
import { RedirectError } from '~/errors/RedirectError';

definePageMeta({
	layout: 'default',
	trackPageView: 'disable',
	scrollToTop: true,
	validate: route => {
		const rootStore = useRootStore();
		return (
			!Array.isArray(route?.meta) ||
			!route.meta.some(
				(metaTag: { domain?: string }) =>
					metaTag.domain && metaTag.domain !== rootStore.topLevelDomain
			)
		);
	}
});

const nuxtApp = useNuxtApp();
// todo check - const cacheStore = useCacheStore();
const relationsStore = useRelationsStore();
const uiStateStore = useUiStateStore();
const jobsStore = useJobsStore();
const loginStore = useLoginStore();
const matchesStore = useMatchesStore();
const rootStore = useRootStore();
const userLandingPageStore = useUserLandingPageStore();
const gncStore = useGncStore();
const route = useRoute();
const userProfileStore = useUserProfileStore();

const branchAutocomplete = useState<InstanceType<typeof Autocomplete> | null>(() => null);
const locationAutocomplete = useState<InstanceType<typeof LocationAutocomplete> | null>(() => null);
const jobPopupHandler = useState<InstanceType<typeof PopupHandler> | null>(() => null);

const {
	appstoreLink,
	currentData,
	currentAvailableFilters,
	hasAvailableFilters,
	searchBarValue,

	getCanonicalUrl,
	getHeaderLinks,
	isJobOverviewPage,
	handleSendStatus,
	maxPastJobs,
	openForm,
	openFilters,
	openJobSearch,
	radius,
	setRadius,
	showOtherTermsSearches,
	updateSelectedFilters,

	activateJobAlarm,

	handleJobSearch,
	handlePageNav,
	jobItemClicked,
	ontologyKeywordSuggestion,
	loadJobData,
	resolveGeoCoords,

	initPageLoadEnd,
	initPageLoadStart,

	commonData,
	jobListData,
	jobListResult,
	jobListResultRelationCountNumber,
	joblistLoading,
	joblistLoadingStates
} = useJobList({
	branchAutocomplete,
	locationAutocomplete,
	jobPopupHandler
});

const { error, clear } = await useAsyncData(route.fullPath, async () => {
	await initPageLoadStart(route);

	// Don't use return result - only here so Nuxt doesn't rerun the request on client side
	return {};
});

if (error.value) {
	const capturedError = error.value.cause instanceof Error ? error.value.cause : error.value;
	const statusCode = error.value?.statusCode || 500;
	clear();

	if (capturedError instanceof RedirectError) {
		nuxtApp.$errorHandler(capturedError, 'Landing page does not exist -> redirecting', {
			level: 'warning',
			sentryExtras: {
				url: route?.fullPath
			},
			tags: { position: 'page_joblist_initPageLoadStart' }
		});

		await navigateTo(
			{
				path: capturedError.toPath,
				query: capturedError.toQuery
			},
			{ redirectCode: capturedError.statusCode ?? 302 }
		);
	} else if (capturedError instanceof PageNotFoundError) {
		nuxtApp.$errorHandler(capturedError, 'No jobs found', {
			level: 'warning',
			sentryExtras: {
				url: route?.fullPath
			},
			tags: { position: 'page_joblist_initPageLoadStart' }
		});

		showError(capturedError);
	} else if (statusCode === 404) {
		nuxtApp.$errorHandler(capturedError, 'Page not found', {
			level: 'error',
			sentryExtras: {
				url: route?.fullPath
			},
			tags: { position: 'page_joblist_initPageLoadStart' }
		});

		showError({
			cause: capturedError,
			statusCode: 404,
			statusMessage: 'Page Not Found',
			data: { isExpectedNotFound: true }
		}); // Return 404 to avoid SEO issues
	} else {
		nuxtApp.$errorHandler(capturedError, 'Joblist page error', {
			level: 'fatal',
			sentryExtras: {
				url: route?.fullPath
			},
			tags: { position: 'page_joblist_initPageLoadStart' }
		});
		showError({
			cause: capturedError,
			statusCode: 500,
			statusMessage: 'Internal Server Error'
		}); // Return 500
	}
}

const recentJobSearches = useState<IRecentJobsearch[]>(() => []);
const jobCategories = useState<{ name: string; link: string }[]>(() => [
	{ name: 'Geringfügig', link: '/jobs/k/geringfügig' },
	{ name: 'Teilzeit', link: '/jobs/k/teilzeit' },
	{ name: 'Vollzeit', link: '/jobs/k/vollzeit' }
]);

const gncCalculation = useState<any | undefined>(() => undefined);
// todo check - const currentYear = new Date().getFullYear();
// todo check - const useNewVersion = useState<boolean>(false);
const scrollDown = useState<boolean>(() => false);
const scrollUp = useState<boolean>(() => false);
const startY = useState<number>(() => 0);

const cvSearchCards = [
	{
		id: 'jetzt_lebenslauf_hochladen',
		headline: 'Welcher Job bist du?',
		firstLine: 'Wir analysieren in nur 3 Minuten deinen Lebenslauf',
		secondLine: 'Du erhältst für dich passende Jobvorschläge!',
		button: 'Jetzt Lebenslauf hochladen'
	},
	{
		id: 'zum_lebenslauf_scan ',
		headline: 'Welcher Job bist du?',
		firstLine: 'Wir analysieren in nur 3 Minuten deinen Lebenslauf',
		secondLine: 'Du erhältst für dich passende Jobvorschläge!',
		button: 'Zum Lebenslauf-Scan'
	}
];
const cvSearchCard = useState(
	() => cvSearchCards[Math.floor(Math.random() * 2)] ?? cvSearchCards[0]
);

const uspCards = [
	{
		headline: 'Dein Profil. <br />Deine Jobs.',
		firstLine: 'Einmal erstellen, für alle Jobs nutzen',
		secondLine: '60% schneller bei jeder hokify Bewerbung'
	},
	{
		headline: 'Entspannte <br />Jobsuche.',
		firstLine: 'Kein Anschreiben notwendig',
		secondLine: 'In nur 3 Minuten bewerben'
	},
	{
		headline: 'Einfache Bewerbung <br />via Smartphone',
		firstLine: 'Traumjob finden, egal wo du gerade bist',
		secondLine: 'Lebenslauf direkt am Handy erstellbar'
	}
];

let indexing: 'noindex' | 'index' = 'index';
if (
	(jobListData.value.version || jobListResult.value?.pageSettings?.noIndex) &&
	!isJobOverviewPage()
) {
	indexing = 'noindex';
}

let deepLink;
if (indexing === 'index' && jobListResult.value?.path) {
	deepLink =
		getCanonicalUrl() ||
		`hokify://jobs/${jobListResult.value?.path?.pageType}${
			jobListResult.value?.path.url
		}?region=${jobListResult.value?.geoRegion?.toLowerCase()}`;
}

const user = computed(() => userProfileStore.obj);

const pageTitle = computed(() => {
	let pageTitleStr = '';
	let jobCount: number = jobListResultRelationCountNumber.value;
	if (jobListData.value.error) {
		pageTitleStr = 'Jobsuche';
		return pageTitleStr;
	}

	if (jobListResult) {
		if (jobListResultRelationCountNumber.value) {
			if (jobListResultRelationCountNumber.value >= 10000) {
				jobCount = 10000;
			} else if (jobListResultRelationCountNumber.value >= 1000) {
				jobCount = Math.floor(jobListResultRelationCountNumber.value / 100) * 100;
			} else if (jobListResultRelationCountNumber.value >= 10) {
				jobCount = Math.floor(jobListResultRelationCountNumber.value / 10) * 10;
			} else {
				jobCount = jobListResultRelationCountNumber.value;
			}
		}
		if (
			commonData.value.showJobDetailMeta &&
			commonData.value.jobDetail &&
			commonData.value.jobDetail.name &&
			commonData.value.jobDetail?.company?.name &&
			!nuxtApp?.$isMobile.any
		) {
			const jobTitle = commonData.value.jobDetail.name.replace('(m/w/d)', '');
			const companyName = commonData.value.jobDetail.company.name.replace(/\s*\[\[.*?\]\]/g, '');
			const titleAndCompanyLength = jobTitle.length + companyName.length;

			if (titleAndCompanyLength < 31) {
				pageTitleStr = `${jobTitle}  ${companyName ? `bei ${companyName}` : ''}  & ähnliche Jobs`;
			} else if (titleAndCompanyLength > 30 && jobTitle.length < 37) {
				pageTitleStr = `${jobTitle} & weitere Jobs `;
			} else {
				pageTitleStr = `${jobTitle}`;
			}
		}
		// title without for joblist without splitpage/ fallback when no jobdetail
		// location and keyword title
		else if (
			jobListData.value.currentLocationName.length &&
			jobListData.value.currentJobField.length
		) {
			const searchtermLength =
				jobListData.value.currentLocationName.length + jobListData.value.currentJobField.length;
			if (searchtermLength > 36) {
				pageTitleStr = `${jobListData.value.currentJobField}  Jobs in ${jobListData.value.currentLocationName} `;
			} else if (searchtermLength > 27 && searchtermLength < 37) {
				pageTitleStr = `${jobCount > 0 ? `${jobCount < 10000 ? `${jobCount}+ ` : jobCount}` : ''} ${
					jobListData.value.currentJobField
				}  Jobs in ${jobListData.value.currentLocationName} `;
			} else {
				pageTitleStr = ` ${
					jobCount > 0 ? `Aktuell ${jobCount < 10000 ? `${jobCount}+ ` : jobCount} ` : 'Aktuelle'
				} ${jobListData.value.currentJobField} Jobs in ${jobListData.value.currentLocationName}`;
			}
		}
		// location only title
		else if (jobListData.value.currentLocationName.length) {
			if (jobListData.value.currentLocationName.length > 19) {
				pageTitleStr = `${
					jobCount > 0 ? `Aktuell ${jobCount < 10000 ? `${jobCount}+ ` : jobCount}` : 'Aktuelle'
				}  Jobs in ${jobListData.value.currentLocationName} `;
			} else if (
				jobListData.value.currentLocationName.length > 10 &&
				jobListData.value.currentLocationName.length < 20
			) {
				pageTitleStr = `Jobs in ${jobListData.value.currentLocationName} | In 3 Minuten bewerben `;
			} else {
				pageTitleStr = `Aktuelle Jobs in ${jobListData.value.currentLocationName} | In 3 Minuten bewerben`;
			}
			// keyword only title
		} else if (jobListData.value.currentJobField.length) {
			if (jobListData.value.currentJobField.length > 23) {
				pageTitleStr = `${
					jobCount > 0 ? `Aktuell ${jobCount < 10000 ? `${jobCount}+ ` : jobCount} ` : 'Aktuelle'
				} ${jobListData.value.currentJobField} Jobs`;
			} else if (
				jobListData.value.currentJobField.length > 14 &&
				jobListData.value.currentLocationName.length < 24
			) {
				pageTitleStr = `${jobListData.value.currentJobField} Jobs | ${
					jobCount > 0
						? `Aktuell ${jobCount < 10000 ? `${jobCount}+ ` : jobCount} offene Stellen`
						: 'In 3 Minuten bewerben '
				}`;
			} else {
				pageTitleStr = `${jobListData.value.currentJobField} Jobs in deiner Nähe | ${
					jobCount > 0
						? `${jobCount < 10000 ? `${jobCount}+ ` : jobCount} Stellen`
						: 'In 3 Minuten bewerben '
				}`;
			}
		} else {
			pageTitleStr = `${
				jobCount < 10000 ? `${jobCount}+ ` : jobCount
			} offene Stellen | In 3 Minuten bewerben`;
		}
	}

	return pageTitleStr;
});

const currDateName = computed(() => format('MMMM yyyy', new Date()));

const pageDescription = computed(() => {
	let pageDescriptionStr = '';
	let jobCount;

	if (jobListResult) {
		if (jobListResultRelationCountNumber.value) {
			if (jobListResultRelationCountNumber.value >= 10000) {
				jobCount = 10000;
			} else if (jobListResultRelationCountNumber.value >= 1000) {
				jobCount = Math.floor(jobListResultRelationCountNumber.value / 100) * 100;
			} else if (jobListResultRelationCountNumber.value >= 10) {
				jobCount = Math.floor(jobListResultRelationCountNumber.value / 10) * 10;
			} else {
				jobCount = jobListResultRelationCountNumber.value;
			}
		}
		if (
			commonData.value.showJobDetailMeta &&
			commonData.value.jobDetail &&
			commonData.value.jobDetail.name &&
			commonData.value.jobDetail.company &&
			commonData.value.jobDetail.company.name &&
			!nuxtApp?.$isMobile.any
		) {
			const jobTitle = commonData.value.jobDetail.name.replace('(m/w/d)', '');
			const companyName = commonData.value.jobDetail.company.name.replace(/\s*\[\[.*?\]\]/g, '');
			const titleAndCompanyLength = jobTitle.length + companyName.length;

			if (titleAndCompanyLength < 31) {
				pageDescriptionStr = `${jobTitle} & weitere Jobs bei hokify - deiner mobilen Jobplattform. ✓ Schnelle & einfache Bewerbung ✓ Gratis Lebenslauf ✓ Ohne Anschreiben `;
			} else if (titleAndCompanyLength > 30 && jobTitle.length < 37) {
				pageDescriptionStr = `Finde ${jobTitle} Jobs ${
					companyName ? `bei ${companyName}` : ''
				} & anderen Top-Unternehmen mit hokify ${
					commonData.value.jobDetail.location &&
					commonData.value.jobDetail.location.countryCode === 'AT'
						? '► Wien, Salzburg, Graz, Linz uvm.'
						: '► Berlin, Köln, München, Stuttgart uvm. '
				}  `;
			} else {
				pageDescriptionStr = `${jobTitle} Jobs ${
					companyName ? `bei ${companyName}` : ''
				} & weiteren Unternehmen ${
					commonData.value.jobDetail.location &&
					commonData.value.jobDetail.location.countryCode === 'AT'
						? '► 10.000+ Stellenangebote in Österreich - hokify'
						: '► 10.000+ Stellenangebote in Deutschland - hokify'
				}`;
			}
		}
		// description without for joblist without splitpage / fallbacks when no jobdetail
		// location and keyword title
		else if (
			jobListData.value.currentLocationName.length &&
			jobListData.value.currentJobField.length
		) {
			const searchtermLength =
				jobListData.value.currentLocationName.length + jobListData.value.currentJobField.length;
			if (searchtermLength > 36) {
				pageDescriptionStr = `Finde deinen neuen ${jobListData.value.currentJobField} Job in ${
					jobListData.value.currentLocationName
				} mit hokify ✓ Dein Profil für deine Jobs. ${
					jobCount > 0 ? `${jobCount < 10000 ? `${jobCount}+ ` : jobCount} offene Stellen im` : '- '
				}  ${currDateName}.`;
			} else if (searchtermLength > 27 && searchtermLength < 37) {
				pageDescriptionStr = `${
					jobCount > 0
						? `${jobCount < 10000 ? `${jobCount < 10000 ? `${jobCount}+ ` : jobCount} ` : jobCount}`
						: ''
				} ${jobListData.value.currentJobField} Jobs in ${
					jobListData.value.currentLocationName
				} warten auf dich. ✓ Top Gehalt ✓ mit & ohne Erfahrung ✓ schnelle Bewerbung mit hokify - ${
					currDateName
				} `;
			} else {
				pageDescriptionStr = `${jobListData.value.currentJobField}  Job in ${
					jobListData.value.currentLocationName
				} gesucht?  ${
					jobCount > 0 ? `Entdecke ${jobCount < 10000 ? `${jobCount}+` : jobCount}` : ''
				}  ✓ Vollzeit ✓ Teilzeit ✓ Nebenjobs uvm. bei hokify. Bewirb dich in 3 Minuten & ganz ohne Anschreiben.`;
			}
		}
		// location only description
		else if (jobListData.value.currentLocationName.length) {
			if (jobListData.value.currentLocationName.length > 19) {
				pageDescriptionStr = `Dein Profil für deine Jobs ► Swipen, chatten, neuer Job - ohne Anschreiben. Entdecke ${
					jobCount > 0 ? `${jobCount < 10000 ? `${jobCount}+ ` : jobCount}` : ''
				} Jobs in ${jobListData.value.currentLocationName}, die zu dir passen - hokify.`;
			} else if (
				jobListData.value.currentLocationName.length > 10 &&
				jobListData.value.currentLocationName.length < 20
			) {
				pageDescriptionStr = `${
					jobCount > 0 ? `Aktuell ${jobCount < 10000 ? `${jobCount}+ ` : jobCount}` : 'Aktuelle'
				} Stellenangebote in ${
					jobListData.value.currentLocationName
				}. Dein Profil. Deine Jobs. Finde jetzt deinen passenden Job mit hokify - ${currDateName} `;
			} else {
				pageDescriptionStr = `${
					jobCount > 0 ? `${jobCount < 10000 ? `${jobCount}+ ` : jobCount}` : ''
				} Jobs in ${
					jobListData.value.currentLocationName
				} verfügbar. Bewirb dich in 3 Minuten ohne Anschreiben - hokify! ✓ Top Gehalt ✓ Home-Office ✓ mit & ohne Erfahrung - ${
					currDateName
				}`;
			}
			// keyword only description
		} else if (jobListData.value.currentJobField.length) {
			if (jobListData.value.currentJobField.length > 23) {
				pageDescriptionStr = `Entdecke ${
					jobCount > 0 ? `über ${jobCount < 10000 ? `${jobCount}+ ` : jobCount}` : ''
				} ${jobListData.value.currentJobField} ${
					jobCount > 0 ? `Stellenangebote` : 'Jobs'
				} in deiner Nähe. ✓ Dein Profil ✓ Deine Jobs. hokify - deine mobile Jobplattform`;
			} else if (
				jobListData.value.currentJobField.length > 14 &&
				jobListData.value.currentLocationName.length < 24
			) {
				pageDescriptionStr = `${jobListData.value.currentJobField} Job gesucht? Finde passende Stellenangebote & bewirb dich in 3 Minuten ohne Anschreiben - hokify. ✓ Vollzeit ✓ Teilzeit ✓ Nebenjobs`;
			} else {
				pageDescriptionStr = `Neuer Job? ${
					jobCount > 0 ? `${jobCount < 10000 ? `${jobCount}+ ` : jobCount}` : ''
				} ${jobListData.value.currentJobField} Jobs - ${
					currDateName
				}. Swipen, chatten, neuer Job - mit hokify in 3 Minuten bewerben. ✓ Vollzeit ✓ Teilzeit ✓ Lehre uvm. `;
			}
		} else {
			pageDescriptionStr = `Neuer Job? Aktuell ${
				jobCount < 10000 ? `${jobCount}+ ` : jobCount
			} Jobs - ${
				currDateName
			}. Swipen, chatten, neuer Job - mit hokify in 3 Minuten bewerben. ✓ Vollzeit ✓ Teilzeit ✓ Lehre uvm. `;
		}
	}
	return pageDescriptionStr;
});

// todo check -
/* const dynamicHeadline = computed(() => {
	return !searchBarValue ? 'h1' : 'h2';
}); */

const jobIsSaved = computed(
	() =>
		matchesStore.getStatusOfRelation({
			type: 'job',
			obj: commonData.value.jobDetail
		}) === 'saved'
);

// deactivated until we adapt it at marekting request
// has to be added to pageDescription again in this case
/* if (jobListData.version === 'lpad') {
	return this.lpadPageDescription;
} */
/* const lpadPageDescription = computed<string>(() => {
	let pageDescriptionPart = '';

	if (jobListResult) {
		const { relationCount } = jobListResult.value?;
		const { branch, city, filters } = jobListResult.value?.selected || {};

		if (!branch?.formatted && !city?.formatted) {
			pageDescriptionPart += 'Aktuelle Jobübersicht von';
			if (filters?.length > 0) {
				pageDescriptionPart += ` ${joinFilters(
					filters.map(f => f.formatted.replace('in der Branche ', '')),
					', ',
					'und',
					filters.length
				)} Jobs ${getCountryByCode(rootStore.topLevelDomain)}.`;
			} else {
				pageDescriptionPart += ' vielen Jobangeboten mit Top Gehalt in deiner Umgebung.';
			}
		} else {
			pageDescriptionPart += 'Alle';
			if (relationCount > 1) {
				pageDescriptionPart += ` ${relationCount}`;
			}

			if (branch?.formatted) {
				pageDescriptionPart += ` ${branch.formatted}`;
			}

			pageDescriptionPart += ' Jobs';
			if (city?.formatted) {
				pageDescriptionPart += ` in ${city.formatted}`;
			} else {
				pageDescriptionPart += ` ${getCountryByCode(rootStore.topLevelDomain)}`;
			}

			pageDescriptionPart += ' bei hokify finden.';
		}
	}

	pageDescriptionPart +=
		' Große Auswahl & Täglich Aktuell. In 3 Minuten erfolgreich für Jobs ohne Anschreiben bewerben. Mit hokify Profil Jobangebote mit Top Gehalt von führenden Arbeitgebern bekommen.';
	return pageDescriptionPart;
}); */

// deactivated until we adapt it at marekting request
// has to be added to pageTitle again in this case
/* const lpadPageTitle = computed<string>(() => {
	const pageTitleParts: string[] = [];

	if (jobListResult) {
		const { branch, city, filters } = jobListResult.value?.selected || {};

		if (branch?.formatted) {
			pageTitleParts.push(`Aktuelle Jobs als ${branch.formatted}`);
		}
		if (city?.formatted) {
			if (branch?.formatted) {
				pageTitleParts.push(`Finde Jobs in ${city.formatted}`);
			} else {
				pageTitleParts.push(`Aktuelle Jobs in ${city.formatted}`);
				pageTitleParts.push(`In 3 Minuten bewerben`);
			}
		} else {
			if (!branch?.formatted) {
				if (filters?.length > 0) {
					pageTitleParts.push(
						`Aktuelle ${joinFilters(
							filters.map(f => f.formatted.replace('in der Branche ', '')),
							', ',
							'und',
							filters.length
						)} Jobs`
					);
				} else {
					pageTitleParts.push(`Aktuelle Jobs`);
				}
			}
			pageTitleParts.push(`Finde Jobs ${getCountryByCode(rootStore.topLevelDomain)}`);
		}
	}

	pageTitleParts.push('hokify Job-Plattform');
	return pageTitleParts.join(' | ');
}); */

const mappedSeoBranches = computed<
	| {
			title: string;
			description: string;
			to?: string;
			linkType?: 'visible' | 'invisible';
	  }[]
	| undefined
>(() => {
	if (userLandingPageStore.popularLandingPages) {
		return userLandingPageStore.popularLandingPages.map(page => ({
			title: page.title,
			description: `${page.recordCount}`,
			to: page.linkUrl,
			linkType: page.linkType
		}));
	}
	return undefined;
});

const mappedSeoCities = computed<
	| {
			title: string;
			description: string;
			to?: string;
			linkType?: 'visible' | 'invisible';
	  }[]
	| undefined
>(() => {
	if (jobListData.value.otherTerms?.refined) {
		return jobListData.value.otherTerms?.refined.map(city => ({
			title: city.name,
			description: `${city.results}`,
			to: city.url,
			linkType: city.linkType
		}));
	}
	return undefined;
});

const mappedSeoRecentJobSearches = computed<
	| {
			title: string;
			description: string;
			to?: string;
			linkType?: 'visible' | 'invisible';
	  }[]
	| undefined
>(() => {
	if (recentJobSearches) {
		return recentJobSearches.value.map(search => ({
			title: search.title,
			description: `${search.recordCount}`,
			to: search.linkUrl,
			linkType: search.linkType
		}));
	}
	return undefined;
});

const mappedSeoRegions = computed<
	| {
			title: string;
			description: string;
			to?: string;
			linkType?: 'visible' | 'invisible';
	  }[]
	| undefined
>(() => {
	if (jobListData.value.otherTerms?.places) {
		return jobListData.value.otherTerms.places.map(place => ({
			title: place.name,
			description: `${place.results}`,
			to: place.url,
			linkType: place.linkType
		}));
	}
	return undefined;
});

const quickFilters = computed<ISelectedFilter[]>(() => {
	const quickFiltersParam: ISelectedFilter[] = [];
	const includedJobtypes = ['jobtype-vollzeit', 'jobtype-teilzeit', 'jobtype-geringfügig'];
	const jobTypesFilter = currentAvailableFilters.value?.Anstellungsart?.filter(filter =>
		includedJobtypes.includes(filter.filter)
	).reverse();
	if (jobTypesFilter) {
		quickFiltersParam.push(...jobTypesFilter);
	}

	const publishTime = currentAvailableFilters.value['Job online seit'];
	/* TODO: remove again when filter naming is changed in the database */
	const publishTimeFilter = publishTime
		?.map(item => {
			if (item.filter === 'publish-hours') {
				return { ...item, title: 'letzte 24 Stunden' };
			}
			if (item.filter === 'publish-days') {
				return { ...item, title: 'letzte 7 Tage' };
			}
			return item;
		})
		.reverse();
	if (publishTimeFilter) {
		quickFiltersParam.push(...publishTimeFilter);
	}
	return quickFiltersParam;
});

const savedMatches = computed<(IAPIMatchForJobSeekerListEntry | IAPIMatchForJobSeeker)[]>(
	() => matchesStore.getAll
);

const seoFooterData = computed<ISeoFooterData[]>(() => [
	{
		title: 'Jobs nach Regionen',
		id: 'regions',
		searches: mappedSeoRegions.value || []
	},
	{
		title: 'Jobs nach Städten',
		id: 'cities',
		searches: mappedSeoCities.value || []
	},
	{
		title: 'Jobs nach Branchen',
		id: 'branches',
		searches: mappedSeoBranches.value || []
	},
	{
		title: 'Deine letzten Suchen',
		id: 'job-searches',
		searches: mappedSeoRecentJobSearches.value || []
	}
]);

const showOtherTermsJobField = computed<boolean>(
	() =>
		!!(
			jobListData.value.otherTerms?.interlinking?.jobField &&
			jobListData.value.otherTerms.interlinking.jobField.length > 0
		)
);

const showOtherTermsLocationHierarchy = computed<boolean>(
	() =>
		!!(
			jobListData.value.otherTerms?.interlinking?.locationHierarchy &&
			jobListData.value.otherTerms.interlinking.locationHierarchy.length > 0
		)
);

const showSeoFooter = computed<boolean>(() => !!commonData.value.jobDetail);

const userImage = computed<string | undefined>(() => user?.value?.images?.small);

// todo check -
/* function alternativeCountry(alternative: {
	url: string;
	countryCode?: string;
	city?: string;
	branch?: string;
}): string {
	if (alternative.countryCode) {
		return getAlternateUrls(rootStore.host, [alternative.countryCode])[0].href + alternative.url;
	}

	return alternative.url;
} */

// todo check -
/* function alternativeLinkText(alternative: {
	url: string;
	countryCode?: string;
	city?: string;
	branch?: string;
}): string {
	let result = '';
	if (alternative.branch) {
		result += `${alternative.branch} `;
	}
	result += 'Jobs';

	if (alternative.city) {
		result += ` in ${alternative.city}`;
	}
	if (alternative.countryCode) {
		if (!alternative.city) {
			result += ' in';
		}
		result += ` ${getCountryByCode(alternative.countryCode)}`;
	}

	result += '?';
	return result;
} */

function companyClicked(company) {
	nuxtApp.$trackWebsiteEvent?.('companyslot_clicked', {
		_id: company._id,
		name: company.name,
		jobfields: company.fields,
		city: company.location?.[0]?.city || undefined,
		audienceId: company.audienceId
	});
}

function filterActive(filter: ISelectedFilter): boolean {
	return jobListData.value.currentSelectedFilters.some(f => f.filter === filter.filter);
}

function goToApp(): void {
	nuxtApp.$trackWebsiteEvent?.('click_get_app', {});
	window.open(appstoreLink.value);
}

function handleScroll() {
	const { scrollY } = window;
	if (scrollY > 210) {
		if (scrollY > startY.value) {
			scrollDown.value = true;
			scrollUp.value = false;
		} else if (scrollY < startY.value) {
			scrollDown.value = false;

			scrollUp.value = true;
		}
		startY.value = scrollY;
	}
}
function jobsearchFeedback(value) {
	// seperate tracking events because an issues with analytics
	switch (value) {
		case 0:
			nuxtApp.$trackWebsiteEvent?.('search_result_usefulness_0', {});
			break;
		case 1:
			nuxtApp.$trackWebsiteEvent?.('search_result_usefulness_1', {});
			break;
		case 2:
			nuxtApp.$trackWebsiteEvent?.('search_result_usefulness_2', {});
			break;
		case 3:
			nuxtApp.$trackWebsiteEvent?.('search_result_usefulness_3', {});
			break;
		default:
			nuxtApp.$trackWebsiteEvent?.('search_result_usefulness_4', {});
			break;
	}
	nuxtApp.$snack.success({
		text: 'Danke für deine Teilnahme an unserer Umfrage.'
	});
}

function mapSuggestionType(type: 'company' | 'keyword') {
	return type === 'company' ? 'Firma' : 'Job';
}

async function openJobSearchPage() {
	await navigateTo('/jobs');
	nuxtApp.$trackWebsiteEvent?.('click_redirect', { url: '/jobs' });
}

function recentJobSearchTitle(): string {
	let jobSearchTitle = '';
	jobSearchTitle += jobListData.value.initialJobField ? jobListData.value.initialJobField : 'Jobs';
	if (jobListData.value.initialLocationName) {
		jobSearchTitle += ` in ${jobListData.value.initialLocationName}`;
	}
	return jobSearchTitle;
}

async function resetFilters() {
	jobListData.value.currentSelectedFilters = [];
	await handleJobSearch();
}

function resetScrollPosition(): void {
	const jobDetailScrollPosition = window?.document?.getElementById('overflow-container');
	if (jobDetailScrollPosition) {
		jobDetailScrollPosition.scrollTop = 0;
	}
}

function setJobalarmTriggered() {
	if (!commonData.value.jobalarmTriggered && jobListData.value.enableNotifications) {
		activateJobAlarm('floating-cta-list');
	}
	commonData.value.jobalarmTriggered = !commonData.value.jobalarmTriggered;
}

function setQuickFilter(filter: ISelectedFilter) {
	updateSelectedFilters(filter);
	trackFilter(filter.filter);
}

function setupSendJob(eventLabel = 'cta-text-right') {
	nuxtApp.$trackWebsiteEvent?.('send_job', {
		pageElement: eventLabel
	});
}

function shortenFilterTitles(filter: ISelectedFilter) {
	let filterTitle = '';
	switch (filter.filter) {
		case 'publish-hours':
			filterTitle = 'letzte 24 Stunden';
			break;
		case 'publish-days':
			filterTitle = 'letzte 7 Tage';
			break;
		case 'wage-0-520.0':
			filterTitle = 'bis 520 €';
			break;
		case 'wage-850.0-1200.0':
			filterTitle = '850 € - 1.200 €';
			break;
		case 'wage-1200.0-2500.0':
			filterTitle = '1.200 € - 2.500 €';
			break;
		case 'wage-2500.0-5300.0':
			filterTitle = '2.500 € - 5.300 €';
			break;
		default:
			filterTitle = filter.title;
			break;
	}
	if (filter.title?.includes('in der Branche')) {
		filterTitle = filter.title?.replace('in der Branche ', '');
	}

	return filterTitle.replace(/['"]+/g, '');
}

function trackCommute(type: 'dropdown' | 'image') {
	nuxtApp.$trackWebsiteEvent?.('click_commute_element', {
		pageElement: type
	});
}

function trackCvSearch() {
	nuxtApp.$trackWebsiteEvent?.('start_search_by_cv:', { elementContent: cvSearchCard.value.id });
}

function trackFilter(type?: string) {
	if (type) {
		nuxtApp.$trackWebsiteEvent?.('click_quick_filter', { type });
	} else {
		nuxtApp.$trackWebsiteEvent?.('click_filter', {});
	}
}

async function checkForTypeCompany(
	_searchTerm: string,
	_event,
	parameters?: { term: string; type: string; data?: { alias?: string } }
) {
	if (parameters?.type === 'company' && parameters?.data?.alias) {
		await navigateTo({ path: `/c/${parameters.data.alias}` });
	}
}

async function activeSourcingInvite(interaction: 'accepted' | 'declined' | 'more-info') {
	nuxtApp.$trackWebsiteEvent?.('active_sourcing_reverse_apply', { interaction });
	if (!commonData.value.jobDetail?._id || !commonData.value.jobDetail?.jobNr) {
		nuxtApp.$snack.danger({
			text: 'Es ist ein Fehler aufgetreten!'
		});
		await navigateTo('/jobs');
	}
	if (interaction === 'accepted') {
		await navigateTo(`/reverse-apply/${commonData.value.jobDetail?._id}`, {
			external: true
		});
	} else if (interaction === 'declined') {
		try {
			await relationsStore.discardRelation({
				type: 'job',
				obj: commonData.value.jobDetail
			});
			if (commonData.value.jobDetail?.selectedCandidateInfo?.status) {
				commonData.value.jobDetail.selectedCandidateInfo.status = 'request-rejected';
			}
			nuxtApp.$snack.success({
				text: 'Das Unternehmen wurde über dein Nicht-Interesse informiert.'
			});
		} catch (err) {
			console.debug('Error while discarding relation', err);
			nuxtApp.$snack.danger({
				text: 'Entscheidung konnte nicht gespeichert werden!'
			});
		}
	} else if (interaction === 'more-info') {
		await navigateTo(`/more-info/${commonData.value.jobDetail?.jobNr}`, {
			external: true
		});
	} else {
		throw new Error('interaction type missing');
	}
}

async function calcGnc(gnc) {
	try {
		gncCalculation.value = await gncStore.sendGnc({ ...gnc, locationUrl: 'job-list' });
		nuxtApp.$trackWebsiteEvent?.('start_calculation', {});
	} catch (err: any) {
		nuxtApp.$errorHandler(err);
	}
}

async function newSearchViaTag(jobField: string) {
	nuxtApp.$trackWebsiteEvent?.('click_jobdetail_tag', {});
	jobListData.value.currentJobField = jobField;
	await handleJobSearch(true);
}

async function rowItemClicked(): Promise<void> {
	// set page number here instead of page navigation in case user clicks on multiple jobs on certain page
	jobsStore.updateLastJobPageNumber({
		page: jobListData.value.currentPage,
		// TOOO check if we have to provide a fallback here (path.url?)
		beautifiedUrl: jobListResult.value?.beautifiedUrl ?? ''
	});
	nuxtApp.$trackWebsiteEvent?.('click_job', {});
	jobItemClicked(jobListResult.value?.lpId);
	commonData.value.jobgone = false;
	resetScrollPosition();
}

async function saveJob(job: IAPIJobPreviewForUser) {
	try {
		const relation = {
			type: 'job',
			obj: job
		};
		let { name } = job;
		if (name.trim().length > 30) {
			name = `${name.trim().slice(0, 30)}...`;
		}
		if (loginStore.loggedIn) {
			if (matchesStore.getStatusOfRelation(relation) !== 'saved') {
				await relationsStore.saveRelation(relation);
				await nuxtApp.$trackWebsiteEvent?.('save_job', {
					jobNr: job.jobNr,
					item_id: job._id,
					item_type: 'job'
				});
				nuxtApp.$snack.success({
					text: `Der  Job "${name}" wurde gespeichert!`
				});
			} else if (matchesStore.getStatusOfRelation(relation) === 'saved') {
				const match = matchesStore.getMatchByRelation(relation);
				await matchesStore.deleteMatch({ match });
				nuxtApp.$snack.success({
					text: `Der gespeicherte Job "${name}" wurde gelöscht!`
				});
			}
		} else {
			openForm('login');
			await nuxtApp.$trackWebsiteEvent?.('save_job', {
				jobNr: job.jobNr,
				item_id: job._id,
				item_type: 'jobNotLoggedIn'
			});
		}
	} catch (err: any) {
		console.debug('Error while saving job', err);
		nuxtApp.$snack.danger({
			text: 'Beim Speichern des Jobs ist ein Fehler aufgetreten. Bitte versuche es später noch einmal.'
		});
	}
}

function getCurrentJobDetailNumberHash(to?: RouteLocationNormalizedGeneric): number | undefined {
	const hash =
		(to ? to.hash : route.hash) || (import.meta.client && !to ? window.location.hash : '');

	if (hash?.startsWith('#job-')) {
		const jobNr = parseInt(hash.slice(5), 10); // remove '#job-' prefix

		if (!isNaN(jobNr)) {
			return jobNr;
		}
	}
	return undefined;
}

async function waitForInitPageLoadStart(): Promise<void> {
	if (!joblistLoadingStates.value.initPageLoadStart) {
		return;
	}

	await new Promise<void>(resolve => {
		const stop = watch(
			() => joblistLoadingStates.value.initPageLoadStart,
			isLoading => {
				if (!isLoading) {
					stop();
					resolve();
				}
			},
			{ immediate: true }
		);
	});
}

async function routeChanged() {
	await waitForInitPageLoadStart();
	await initPageLoadEnd();
	commonData.value.showJobDetailMeta = true;
}

onBeforeMount(() => {
	commonData.value.currentEmail = user.value?.general?.email ?? null;
	commonData.value.currentPhone = user.value?.general?.phone ?? null;
	commonData.value.isCompanyPage = false;
});

onMounted(async () => {
	await waitForInitPageLoadStart();
	await initPageLoadEnd();

	let fromRoute = 'default' as 'default' | 'gnc' | 'startpage';
	// from.name because on refresh from.path is always set '/' which causes the element
	const fromName = route?.redirectedFrom?.name ?? ''; // or use your own global ref
	if (fromName === 'index') {
		fromRoute = 'startpage';
	} else if (fromName === 'lp-brutto-netto-rechner') {
		fromRoute = 'gnc';
	}

	if (!uiStateStore.fromRoute) {
		uiStateStore.updateFromRoute(fromRoute);
	}

	window.addEventListener('scroll', handleScroll);
	if (
		!route.path.split('?')[0].endsWith('/jobs') &&
		jobListResult.value?.beautifiedUrl &&
		!route.path.includes('/f/') &&
		!route.path.includes('/c/')
	) {
		const newSearch = {
			linkUrl: decodeURI(jobListResult.value?.beautifiedUrl),
			title: recentJobSearchTitle(),
			recordCount: jobListResultRelationCountNumber.value
		};
		recentJobSearches.value = saveToRecentJobSearches(newSearch);
	}
	if (lsTest()) {
		const storage = localStorage.getItem('recent-jobsearches');
		if (typeof storage === 'string') {
			recentJobSearches.value = JSON.parse(storage);
		}
	}
	if (
		nuxtApp.$isMobile.any &&
		!jobListData.value.enableNotifications &&
		jobListResult.value?.path?.url
	) {
		nuxtApp.$trackWebsiteEvent?.('web_to_app_banner_view', {});
	}
	if (!loginStore.loggedIn && cvSearchCard) {
		nuxtApp.$trackWebsiteEvent?.('view_search_by_cv:', { elementContent: cvSearchCard.value.id });
	}
});

onBeforeUnmount(() => {
	commonData.value.observer?.disconnect();
	window.removeEventListener('scroll', handleScroll);
});

const result: any = {
	title: pageTitle,
	titleTemplate: '%s | hokify',
	link: getHeaderLinks(),
	meta: [
		{ name: 'robots', content: `${indexing}, follow` },
		{ name: 'description', content: pageDescription }
	]
};

result.meta.push(
	...getSocialMediaMetaData(result, rootStore.topLevelDomain, undefined, deepLink, route.query)
);

useHead(result);

watch(
	() => route.query,
	async () => {
		await routeChanged();
	}
);

watch(
	() => route.fullPath,
	(newFullPath, oldFullPath) => {
		if (!newFullPath || newFullPath === oldFullPath || !lsTest()) {
			return;
		}

		saveLastJobsearchPath(newFullPath);
	},
	{ immediate: true }
);

onBeforeRouteLeave(async (to, from, next) => {
	if (
		nuxtApp.$isMobile.any ||
		from.path.startsWith('/c/') ||
		!to.path.startsWith('/job/') ||
		!to.params?.job
	) {
		next();
		return;
	}

	const hash = to.params?.job ? `#job-${to.params.job}` : undefined;

	next({
		path: from.path,
		query: from.query,
		hash
	});
});

onBeforeRouteUpdate(async (to, _from, next) => {
	const jobNr = getCurrentJobDetailNumberHash(to);
	if (jobNr) {
		// if we are on a mobile device redirect to jobdetail page instead
		if (nuxtApp.$isMobile.phone) {
			return next(`/job/${jobNr}`);
		}

		if (jobNr && commonData.value.jobDetail?.jobNr !== jobNr) {
			await loadJobData(jobNr);
		}
	}

	next();
});
</script>

<style scoped lang="scss">
@use '@/assets/styles/mixins/effects.scss';
// ugly but necessary, until we refactor our info/error - box
.reset-margin {
	margin-bottom: 0 !important;
}
// double ugly but okay since its only a test - if we wanna implement this, we will need to refactor info-box
.overwrite-margin {
	margin-bottom: 1rem !important;
}
.highlight {
	@include effects.highlight(0.2s, 1.04);
}
.slide-fade-enter {
	opacity: 0;
}
.slide-fade-enter-active {
	transition: opacity 0.3s;
}
.slide-fade-leave-active {
	transition: opacity 0.3s;
	opacity: 0;
}

.hide-scrollbar::-webkit-scrollbar {
	display: none;
}

.hide-scrollbar {
	-ms-overflow-style: none;
	scrollbar-width: none;
}

.fade-enter-active,
.fade-leave-active {
	transition: opacity 0.5s;
}
.fade-enter,
.fade-leave-to {
	opacity: 0;
}
</style>
